import type * as THREE from 'three';
import { copyMaterial } from '../world.js';

export type Opacity = { base?: number; fade?: number; light?: boolean };

/** A material's opacity from its two owners: the colour block's (`base`) times the show block's (`fade`). */
export function setOpacity(material: THREE.Material, change: Opacity): void {
  const own = material.userData as { base?: number; fade?: number; light?: boolean };
  if (change.base !== undefined) own.base = change.base;
  if (change.fade !== undefined) own.fade = change.fade;
  if (change.light !== undefined) own.light = change.light;
  const opacity = (own.base ?? 1) * (own.fade ?? 1);
  material.opacity = opacity;
  // Transparent only while something makes it so (a light always is).
  material.transparent = own.light === true || opacity < 1;
}

/**
 * The package's GLB meshes by name, for the blocks that act on named parts (draaien, zichtbaar, kleur,
 * route): a block asks once with its prefixes and is handed the matching meshes of every GLB, the ones
 * already here at once and later ones as they arrive (decision 4).
 */
export class PartRegistry {
  readonly #meshes: THREE.Mesh[] = [];
  readonly #own = new WeakMap<THREE.Mesh, THREE.Material[]>();
  readonly #copies = new WeakMap<THREE.Material, THREE.Material>();
  readonly #waiting: { prefixes: readonly string[]; take: (meshes: THREE.Mesh[]) => void }[] = [];

  /**
   * A mesh's own materials: copies of the GLB's, made once and shared by every block that acts on the
   * part (kleur, zichtbaar, route), so their effects add up instead of one replacing the other. Meshes
   * that shared a material share its copy too.
   */
  materialsOf(mesh: THREE.Mesh): THREE.Material[] {
    let own = this.#own.get(mesh);
    if (!own) {
      const given = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      own = given.map((source) => {
        let copy = this.#copies.get(source);
        if (!copy) {
          // Keeping the material's sway (a surface block's foliage()) and its late maps.
          copy = copyMaterial(source);
          this.#copies.set(source, copy);
        }
        return copy;
      });
      this.#own.set(mesh, own);
      mesh.material = Array.isArray(mesh.material) ? own : own[0]!;
    }
    return own;
  }

  /** Another kind of material for the part (kleur's light look): what the blocks now act on. */
  replaceMaterials(mesh: THREE.Mesh, materials: THREE.Material[]): void {
    this.#own.set(mesh, materials);
    mesh.material = Array.isArray(mesh.material) ? materials : materials[0]!;
  }

  add(meshes: readonly THREE.Mesh[]): void {
    this.#meshes.push(...meshes);
    for (const wait of this.#waiting) {
      const found = meshes.filter((m) => wait.prefixes.some((p) => m.name.startsWith(p)));
      if (found.length > 0) wait.take(found);
    }
  }

  when(prefixes: readonly string[], take: (meshes: THREE.Mesh[]) => void): void {
    this.#waiting.push({ prefixes, take });
    const found = this.#meshes.filter((m) => prefixes.some((p) => m.name.startsWith(p)));
    if (found.length > 0) take(found);
  }
}
